import {createContext, useContext, useEffect, useState} from 'react';
import {api, externalContactEndpoints, externalContactTypeEndpoints, getApiErrorMessage} from '@/lib/api';
import {useAuth} from '@/contexts/AuthContext';

const Ctx = createContext(null);

const collectionPayload = (payload, key) => {
    const source = payload?.data ?? payload;
    if (Array.isArray(source)) return source;
    if (Array.isArray(source?.data)) return source.data;
    if (Array.isArray(source?.[key])) return source[key];
    return [];
};

const contactTypeKey = (contactType) =>
    String(contactType?.id || contactType?.external_contact_type_id || contactType?.key || contactType?.slug || contactType?.name || '').trim();

const normalizeContactTypes = (payload) => {
    const source = payload?.data ?? payload;
    const map = source?.contact_types ?? source?.contactTypes ?? source?.external_contact_types ?? source?.externalContactTypes ?? source;

    if (map && !Array.isArray(map) && typeof map === 'object') {
        return Object.entries(map).reduce((acc, [key, contactType]) => {
            if (contactType && typeof contactType === 'object') {
                acc[key] = {
                    label: contactType.label || contactType.name || contactType.title || key,
                    color: contactType.color || contactType.hex_color || contactType.hexColor || '#64748b',
                };
            } else {
                acc[key] = {label: String(contactType || key), color: '#64748b'};
            }
            return acc;
        }, {});
    }

    return collectionPayload(payload, 'contact_types').reduce((acc, contactType) => {
        const key = contactTypeKey(contactType);
        if (!key) return acc;
        acc[key] = {
            label: contactType.label || contactType.name || contactType.title || key,
            color: contactType.color || contactType.hex_color || contactType.hexColor || '#64748b',
        };
        return acc;
    }, {});
};

const normalizeResourceIds = (source) => {
    const resources = source?.resourceIds ?? source?.resource_ids ?? source?.resources ?? [];
    if (!Array.isArray(resources)) return [];
    return resources.map(resource => (
        typeof resource === 'string' || typeof resource === 'number'
            ? String(resource)
            : String(resource?.id || resource?.resource_id || '')
    )).filter(Boolean);
};

const toApiId = (value) => {
    if (value === undefined || value === null || value === '') return null;
    const number = Number(value);
    return Number.isInteger(number) ? number : value;
};

const normalizeContact = (source = {}, fallback = {}) => ({
    ...fallback,
    ...source,
    id: source.id ?? source.external_contact_id ?? source.contact_id ?? fallback.id,
    name: source.name ?? fallback.name ?? '',
    nickname: source.nickname ?? source.nick_name ?? fallback.nickname ?? '',
    phone: source.phone ?? source.phone_number ?? fallback.phone ?? '',
    email: source.email ?? fallback.email ?? '',
    category: String(source.external_contact_type_id ?? source.contact_type_id ?? source.contactTypeId ?? source.contact_type ?? source.contact_type_key ?? source.contactType ?? source.category ?? source.category_key ?? source.category_slug ?? fallback.category ?? ''),
    organization: source.organization ?? source.organisation ?? source.company ?? fallback.organization ?? '',
    notes: source.notes ?? source.note ?? fallback.notes ?? '',
    resourceIds: normalizeResourceIds(source).length ? normalizeResourceIds(source) : (fallback.resourceIds || []),
});

const normalizeContacts = (payload) =>
    collectionPayload(payload, 'contacts').map((contact, index) => normalizeContact(contact, {id: index + 1, resourceIds: []}));

const contactPayload = (data) => ({
    name: data.name,
    nickname: data.nickname || null,
    phone: data.phone || null,
    email: data.email || null,
    external_contact_type_id: data.category ? Number(data.category) : null,
    contact_type_id: data.category ? Number(data.category) : null,
    organization: data.organization || null,
    notes: data.notes || null,
    resource_ids: (data.resourceIds || []).map(toApiId).filter(id => id !== null),
});

export const ExternalContactsProvider = ({children}) => {
    const {user, ready} = useAuth();
    const [contacts, setContacts] = useState([]);
    const [contactTypes, setContactTypes] = useState({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        let cancelled = false;

        if (!ready) return () => {
            cancelled = true;
        };

        // First-login users have a session, but the API still restricts them to
        // password/email activation endpoints. Do not preload protected data.
        if (!user || user.mustChangePassword || !user.emailVerified) {
            setContacts([]);
            setContactTypes({});
            setError('');
            setLoading(false);
            return () => {
                cancelled = true;
            };
        }

        const loadExternalContacts = async () => {
            setLoading(true);
            setError('');

            try {
                const [contactsResponse, contactTypesResponse] = await Promise.all([
                    api.get(externalContactEndpoints.list),
                    api.get(externalContactTypeEndpoints.list),
                ]);

                if (!cancelled) {
                    setContacts(normalizeContacts(contactsResponse.data));
                    setContactTypes(normalizeContactTypes(contactTypesResponse.data));
                }
            } catch (requestError) {
                if (!cancelled) {
                    setContacts([]);
                    setContactTypes({});
                    setError(getApiErrorMessage(requestError, 'Não foi possível carregar os contactos.'));
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        loadExternalContacts();

        return () => {
            cancelled = true;
        };
    }, [ready, user]);

    const addContact = async (data) => {
        const response = await api.post(externalContactEndpoints.create, contactPayload(data));
        const fallback = {
            id: Date.now(),
            resourceIds: [],
            ...data,
        };
        const next = normalizeContact(response.data?.data ?? response.data?.contact ?? response.data, fallback);
        setContacts(prev => [next, ...prev]);
        return next;
    };

    const updateContact = async (id, patch) => {
        const response = await api.post(externalContactEndpoints.update, {
            id,
            external_contact_id: id,
            ...contactPayload(patch),
        });
        let updated;
        setContacts(prev => prev.map(contact => {
            if (contact.id !== id) return contact;
            updated = normalizeContact(response.data?.data ?? response.data?.contact ?? response.data, {...contact, ...patch});
            return updated;
        }));
        return updated;
    };

    const removeContact = async (id) => {
        await api.post(externalContactEndpoints.delete, {
            id,
            external_contact_id: id,
        });
        setContacts(prev => prev.filter(contact => contact.id !== id));
    };

    const findByPhone = (phone) => contacts.find(c => (c.phone || '').replace(/\s/g, '') === (phone || '').replace(/\s/g, ''));

    return (
        <Ctx.Provider value={{contacts, contactTypes, loading, error, addContact, updateContact, removeContact, findByPhone}}>
            {children}
        </Ctx.Provider>
    );
};

export const useExternalContacts = () => {
    const ctx = useContext(Ctx);
    if (!ctx) throw new Error('useExternalContacts must be used within ExternalContactsProvider');
    return ctx;
};
